import React from "react";
import ShareholderFields from "./ShareholderFields";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { useWatch } from "react-hook-form";
import Divider from "components/Divider/Divider";

const PAGE_LOCALE_KEY = "page.merchants.";
const FORM_LABEL_KEY =
  PAGE_LOCALE_KEY + "merchantDetail.info.companyInfo.fields";

const OWNER_COUNTRY_OPTIONS = [
  {
    key: "hk",
    title: "Hong Kong",
    value: "HK",
  },
  // Deprecated option, commented for possible future use
  // {
  //   key: "sg",
  //   title: "Singapore",
  //   value: "SG",
  // },
]
const OWNERSHIP_TYPE_OPTIONS =
  [
    {
      key: "Limited Company",
      title: "Limited Company",
      value: "Limited Company",
    },
    {
      key: "Sole-proprietorship",
      title: "Sole-proprietorship",
      value: "Sole-proprietorship",
    },
    {
      key: "Partnership",
      title: "Partnership",
      value: "Partnership",
    },
    {
      key: "Others",
      title: "Others",
      value: "Others",
    },
  ]
const BUSINESS_NATURE_OPTIONS = [
  {
    key: "Restaurant",
    title: "Restaurant",
    value: "Restaurant",
  },
  {
    key: "Retail",
    title: "Retail",
    value: "Retail",
  },
  {
    key: "Services",
    title: "Services",
    value: "Services",
  },
]
const BUSINESS_PLAN_OPTIONS = [
  {
    key: "sme",
    title: "SME",
    value: "sme",
  },
  {
    key: "normal",
    title: "Standard",
    value: "normal",
  },
]

const CompanyInfoSession = ({
  formProps,
  isCreate,
  isEdit,
  mccDropdownOptions,
  isLoading,
}) => {
  const { t } = useTranslation();
  const ownershipType = useWatch({ control: formProps.control, name: "ownershipType", });

  const isDisabled = !isEdit || isLoading;

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    ownerName: {
      fieldType: "textInput",
      fieldName: "ownerName",
      label: t(`${FORM_LABEL_KEY}.ownerName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 50,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    ownerNameSecondaryLang: {
      fieldType: "textInput",
      fieldName: "ownerNameSecondaryLang",
      label: t(`${FORM_LABEL_KEY}.ownerNameSecondaryLang`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
        ],
      },
    },
    dbaName: {
      fieldType: "textInput",
      fieldName: "name",
      label: t(`${FORM_LABEL_KEY}.name`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 22,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    dbaNameSecondaryLang: {
      fieldType: "textInput",
      fieldName: "nameSecondaryLang",
      label: t(`${FORM_LABEL_KEY}.nameSecondaryLang`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
        ],
      },
    },

    ownerCountry: {
      fieldType: "selection",
      fieldName: "ownerCountry",
      label: t(`${FORM_LABEL_KEY}.ownerCountry`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "HK",
        options: OWNER_COUNTRY_OPTIONS
      },
    },

    ownerAddress1: {
      fieldType: "textInput",
      fieldName: "ownerAddress1",
      label: t(`${FORM_LABEL_KEY}.ownerAddress1`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "onlyAlphabetNumAndSpaces",
        ],
      },
    },
    ownerAddress2: {
      fieldType: "textInput",
      fieldName: "ownerAddress2",
      label: t(`${FORM_LABEL_KEY}.ownerAddress2`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "onlyAlphabetNumChineseAndSpaces",
        ],
      },
    },
    ownershipType: {
      fieldType: "selection",
      fieldName: "ownershipType",
      label: t(`${FORM_LABEL_KEY}.ownershipType`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        options: OWNERSHIP_TYPE_OPTIONS
      },
    },
    ciNumber: {
      fieldType: "textInput",
      fieldName: "ciNumber",
      label: t(`${FORM_LABEL_KEY}.ciNumber`),
      mandatory: ownershipType === "Limited Company",
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noQuotesOrBackticks",
          "onlyInteger",
          "noLeadingWhitespace",
        ],
      },
    },

    businessEstablishedDate: {
      fieldType: "datePicker",
      fieldName: "businessEstablishedDate",
      label: t(`${FORM_LABEL_KEY}.businessEstablishedDate`),
      mandatory: ownershipType === "Limited Company",
      disabled: isDisabled,
      config: {
        defaultValue: new Date(),
        maxDate: dayjs(),
        rules: [
          "validDate",
          "onlyBeforeToday"
        ]
      },
    },

    merchantCategoryCode: {
      fieldType: "selection",
      fieldName: "merchantCategoryCode",
      label: t(`${FORM_LABEL_KEY}.merchantCategoryCode`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        options: mccDropdownOptions ?? [],
      },
    },
    businessNature: {
      fieldType: "selection",
      fieldName: "businessNature",
      label: t(`${FORM_LABEL_KEY}.businessNature`),
      disabled: isDisabled,
      config: {
        options: BUSINESS_NATURE_OPTIONS
      },
    },

    contactPersonName: {
      fieldType: "textInput",
      fieldName: "contactPerson",
      label: t(`${FORM_LABEL_KEY}.contactPerson`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    contactPersonPhone: {
      fieldType: "textInput",
      fieldName: "phone",
      label: t(`${FORM_LABEL_KEY}.phone`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: [
          "validPhoneNumber",
        ],
        sanitizers: ["numericOnly"]
      },
    },
    contactPersonEmail: {
      fieldType: "textInput",
      fieldName: "email",
      label: t(`${FORM_LABEL_KEY}.email`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: [
          "validEmail",
        ],
      },
    },

    businessPlan: {
      fieldType: "selection",
      fieldName: "businessPlan",
      label: t(`${FORM_LABEL_KEY}.businessPlan`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "normal",
        options: BUSINESS_PLAN_OPTIONS
      },
    },
    isoAgentEmail: {
      fieldType: "textInput",
      fieldName: "isoAgentEmail",
      label: t(`${FORM_LABEL_KEY}.isoAgentEmail`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: [
          "validEmail",
        ]
      },
    },
    isoAgentId: {
      fieldType: "textInput",
      fieldName: "isoAgentId",
      label: t(`${FORM_LABEL_KEY}.isoAgentId`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: [
          "onlyInteger",
        ]
      },
    }
  }

  return (
    <FormBuilder.Section
      title={t(`${PAGE_LOCALE_KEY}merchantDetail.info.companyInfo.title`)}
      titleVariant="h3">
      <FormBuilder.Row itemsPerRow={2}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.ownerName,
              fieldsConfig.ownerNameSecondaryLang,
              fieldsConfig.dbaName,
              fieldsConfig.dbaNameSecondaryLang,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.ownerCountry,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={2}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.ownerAddress1,
              fieldsConfig.ownerAddress2,
              fieldsConfig.ownershipType,
              fieldsConfig.ciNumber,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.businessEstablishedDate,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.merchantCategoryCode,
              fieldsConfig.businessNature,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={3}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.contactPersonName,
              fieldsConfig.contactPersonPhone,
              fieldsConfig.contactPersonEmail,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.businessPlan,
              !isCreate && fieldsConfig.isoAgentId,
            ]
          })
        }
      </FormBuilder.Row>
      <Divider />
      <ShareholderFields formProps={formProps} disabled={isDisabled} />
    </FormBuilder.Section>
  );
};
export default React.memo(CompanyInfoSession);
